import { birthdateToDate } from './birthdate';

/**
 * O(1) duplicate-patient lookup for the Add Patient flow.
 *
 * SHER-194: the previous implementation called `.find()` over the full
 * decrypted patient list on every submit, doing two console.log and four
 * string normalisations per patient. On CHOM's 49,992-patient DB that was
 * ~30-60s per submit (worse with DevTools open).
 *
 * This module builds an index once per Redux patients-array reference
 * (via useMemo in the caller) and looks up candidates in O(1) at submit.
 */

export interface DedupCandidate {
    uuid: string;
    personalData: {
        name?: string;
        surnames?: string;
        birthdate?: any;
        health_id?: string;
        [k: string]: any;
    };
}

export interface DedupIndex {
    /** Map<normalizedHealthId, patient> — fast path; empty when no patient has a health_id. */
    byHealthId: Map<string, DedupCandidate>;
    /** Map<`${name}|${surname}|${dobKey}`, patient[]> — fallback when health_id is missing. */
    byNameDob: Map<string, DedupCandidate[]>;
}

/** Lowercase, strip accents (NFD + combining marks), collapse whitespace. */
export function normalizeName(input: unknown): string {
    if (typeof input !== 'string') return '';
    return input
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
}

/** Canonical YYYY-MM-DD string for any supported birthdate shape, or null. */
export function dobKey(raw: unknown): string | null {
    const d = birthdateToDate(raw as any);
    if (!d) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

export function buildDedupIndex(patients: DedupCandidate[] | null | undefined): DedupIndex {
    const byHealthId = new Map<string, DedupCandidate>();
    const byNameDob = new Map<string, DedupCandidate[]>();

    if (!patients?.length) return { byHealthId, byNameDob };

    for (const pat of patients) {
        const pd = pat?.personalData;
        if (!pd) continue;

        if (typeof pd.health_id === 'string' && pd.health_id.length > 0) {
            byHealthId.set(pd.health_id, pat);
        }

        const name = normalizeName(pd.name);
        const surname = normalizeName(pd.surnames);
        const dob = dobKey(pd.birthdate);
        if (name && surname && dob) {
            const key = `${name}|${surname}|${dob}`;
            const bucket = byNameDob.get(key);
            if (bucket) bucket.push(pat);
            else byNameDob.set(key, [pat]);
        }
    }

    return { byHealthId, byNameDob };
}

export interface CandidateInput {
    name?: string;
    surnames?: string;
    birthdate?: any;
    health_id?: string;
    [k: string]: any;
}

/** Returns the first matching existing patient, or null. */
export function findDuplicatePatient(
    index: DedupIndex,
    candidate: CandidateInput
): DedupCandidate | null {
    if (typeof candidate.health_id === 'string' && candidate.health_id.length > 0) {
        const hit = index.byHealthId.get(candidate.health_id);
        if (hit) return hit;
    }

    const name = normalizeName(candidate.name);
    const surname = normalizeName(candidate.surnames);
    const dob = dobKey(candidate.birthdate);
    if (!name || !surname || !dob) return null;

    const bucket = index.byNameDob.get(`${name}|${surname}|${dob}`);
    return bucket && bucket.length > 0 ? bucket[0] : null;
}
